import { useState } from 'react';
import type { BrokerAccountApprovalReview, BrokerAccountTradingAccess } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useAuth } from '@/context/AuthContext';
import { Button, FormCheckbox, FormInput, Modal } from '@/components/UI';

const statusLabels: Record<BrokerAccountTradingAccess['status'], string> = {
    paper: 'Paper account',
    ownerExempt: 'Owner account — size approval exempt',
    ownershipReviewRequired: 'Holdings need reconciliation',
    unchecked: 'Value check needed',
    unavailable: 'Verification unavailable — new trades blocked',
    belowLimit: 'Within limit at last check',
    approvalRequired: 'Administrator approval required',
    approved: 'Approved by administrator',
    revoked: 'New trades blocked by administrator'
};
const money = (value: number | null) =>
    value === null ? 'Unavailable' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function BrokerAccountApprovals() {
    const { user, sessionPolicy } = useAuth();
    const [reviewAll, setReviewAll] = useState(false);
    const [afterId, setAfterId] = useState(0);
    const [checked, setChecked] = useState<Record<number, BrokerAccountTradingAccess>>({});
    const [selected, setSelected] = useState<{ account: BrokerAccountApprovalReview; decision: 'approved' | 'revoked' } | null>(null);
    const [reason, setReason] = useState('');
    const [resolution, setResolution] = useState<{ accountId: number; positionId: number } | null>(null);
    const [outcome, setOutcome] = useState<'holdingsVerified' | 'brokerClose' | 'expiration' | 'assignment' | 'exercise'>(
        'holdingsVerified'
    );
    const [brokerReference, setBrokerReference] = useState('');
    const [quantity, setQuantity] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [fees, setFees] = useState('0');
    const [acknowledged, setAcknowledged] = useState(false);
    const isAdmin = user?.type === 9 && sessionPolicy.canManageBrokerConnections;
    const admin = isAdmin && reviewAll;
    const query = useAppQuery(
        admin
            ? trpc.admin.listBrokerAccountApprovals.queryOptions({ afterId })
            : trpc.user.listBrokerAccountApprovals.queryOptions({ afterId })
    );
    const check = useTrpcMutation(
        admin ? trpc.admin.checkBrokerAccountApproval.mutationOptions() : trpc.user.checkBrokerAccountApproval.mutationOptions(),
        { fallbackErrorMessage: 'Could not check account value' }
    );
    const decide = useTrpcMutation(
        trpc.admin.decideBrokerAccountApproval.mutationOptions({
            onSuccess: () => {
                setSelected(null);
                setChecked({});
                void query.refetch();
            }
        }),
        { successMessage: 'Account trading permission updated', fallbackErrorMessage: 'Could not update trading permission' }
    );

    const reconcile = useTrpcMutation(
        trpc.admin.reconcileBrokerPosition.mutationOptions({
            onSuccess: () => {
                setResolution(null);
                setChecked({});
                void query.refetch();
            }
        }),
        { successMessage: 'Broker position reconciled', fallbackErrorMessage: 'Could not reconcile broker evidence' }
    );
    const isSettlement = outcome === 'expiration' || outcome === 'assignment' || outcome === 'exercise';

    const checkAccount = (account: BrokerAccountApprovalReview, review: boolean) => {
        check.mutate(
            { tradingAccountId: account.tradingAccountId },
            {
                onSuccess: (result) => {
                    setChecked((previous) => ({ ...previous, [account.tradingAccountId]: result.access }));
                    if (review && result.access.status !== 'unavailable') {
                        setReason('');
                        setAcknowledged(false);
                        setSelected({ account: { ...account, access: result.access }, decision: 'approved' });
                    }
                }
            }
        );
    };

    return (
        <section className="my-4 rounded border border-gray-700 p-4" aria-label="Live trading permission">
            <h4 className="text-lg font-semibold">Live trading permission</h4>
            <p className="mt-2 text-sm text-gray-300">
                Third-party live accounts above $50,000 require explicit administrator approval of account size. The limit uses the higher
                of net account value and total holdings value, including holdings outside Project314. Accounts must be dedicated to
                Project314, with permitted cash reserves. Unexplained holdings block new entries at any size. The platform owner’s accounts
                are exempt from size approval and dedicated-account restrictions. Every live account requires broker-confirmed settlement;
                unresolved managed positions block new entries. Closing orders and cancellations remain available. Importing or reconnecting
                an account does not grant approval.
            </p>
            {isAdmin && (
                <div className="mt-3">
                    <FormCheckbox
                        label="Review all users’ accounts as administrator"
                        checked={reviewAll}
                        onChange={(event) => {
                            setReviewAll(event.target.checked);
                            setAfterId(0);
                            setChecked({});
                        }}
                    />
                </div>
            )}
            {query.isLoading && <p className="mt-3">Loading trading permissions…</p>}
            {query.error && (
                <p role="alert" className="mt-3 text-red-400">
                    {query.error.message}
                </p>
            )}
            <div className="mt-3 space-y-3">
                {query.data?.accounts.map((account) => {
                    const access = checked[account.tradingAccountId] ?? account.access;
                    return (
                        <div className="rounded bg-gray-800 p-3" key={account.tradingAccountId}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <p className="font-medium">
                                        {account.name} · {account.brokerage} · Account #{account.tradingAccountId}
                                        {admin ? ` · User ${account.userId}` : ''}
                                    </p>
                                    <p className={access.canOpenPositions ? 'text-green-300' : 'text-amber-300'}>
                                        {statusLabels[access.status]}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-300">{access.message}</p>
                                    {access.checkedAt !== null && (
                                        <p className="mt-1 text-sm text-gray-400">
                                            Net value {money(access.equity)} · Holdings {money(access.grossPositionValue)} · Checked{' '}
                                            {new Date(access.checkedAt * 1000).toLocaleString()}
                                        </p>
                                    )}
                                    {access.decidedAt !== null && (
                                        <p className="text-sm text-gray-400">
                                            Decision by administrator #{access.adminUserId} ·{' '}
                                            {new Date(access.decidedAt * 1000).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                                {access.status !== 'paper' && sessionPolicy.canManageBrokerConnections && (
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            variant="secondary"
                                            disabled={check.isPending || decide.isPending}
                                            onClick={() => {
                                                checkAccount(account, false);
                                            }}
                                        >
                                            Check holdings
                                        </Button>
                                        {admin && account.access.status !== 'ownerExempt' && (
                                            <>
                                                <Button
                                                    variant="primary"
                                                    disabled={check.isPending || decide.isPending}
                                                    onClick={() => {
                                                        checkAccount(account, true);
                                                    }}
                                                >
                                                    Review account size
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    disabled={check.isPending || decide.isPending}
                                                    onClick={() => {
                                                        setReason('');
                                                        setAcknowledged(false);
                                                        setSelected({ account: { ...account, access }, decision: 'revoked' });
                                                    }}
                                                >
                                                    Revoke
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            {access.holdings && (
                                <div className="mt-3 overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr>
                                                <th>Instrument / side</th>
                                                <th>Broker quantity</th>
                                                <th>Managed quantity</th>
                                                <th>Difference</th>
                                                <th>Review</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {access.holdings.map((holding) => (
                                                <tr key={`${holding.assetClass}:${holding.symbol}:${holding.side}`}>
                                                    <td className="py-2">
                                                        {holding.symbol} · {holding.side} · {holding.assetClass}
                                                    </td>
                                                    <td>{holding.brokerQuantity}</td>
                                                    <td>{holding.managedQuantity}</td>
                                                    <td>{holding.difference}</td>
                                                    <td>
                                                        {holding.cashReserve
                                                            ? 'Permitted cash reserve · unmanaged'
                                                            : holding.difference !== 0
                                                              ? 'Unexplained quantity'
                                                              : 'Quantities match'}
                                                        {holding.pendingPositionIds.map((positionId) => (
                                                            <div key={positionId}>
                                                                Position #{positionId} awaiting reconciliation
                                                                {admin && (
                                                                    <Button
                                                                        variant="secondary"
                                                                        onClick={() => {
                                                                            setResolution({
                                                                                accountId: account.tradingAccountId,
                                                                                positionId
                                                                            });
                                                                            setOutcome('holdingsVerified');
                                                                            setBrokerReference('');
                                                                            setReason('');
                                                                            setAcknowledged(false);
                                                                            setQuantity('');
                                                                            setEventTime('');
                                                                            setFees('0');
                                                                        }}
                                                                    >
                                                                        Reconcile
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="mt-2 text-xs text-gray-400">
                                        Quantities use shares, option contracts, or cryptocurrency units. Cash reserves are included in
                                        account value and are never automatically sold.
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                })}
                {query.data?.accounts.length === 0 && <p className="text-sm text-gray-400">No accounts on this page.</p>}
            </div>
            <div className="mt-3 flex gap-2">
                {afterId > 0 && (
                    <Button variant="secondary" onClick={() => setAfterId(0)}>
                        First page
                    </Button>
                )}
                {query.data?.nextId != null && (
                    <Button variant="secondary" onClick={() => setAfterId(query.data?.nextId ?? 0)}>
                        Next page
                    </Button>
                )}
            </div>
            <Modal
                isOpen={selected !== null}
                title={selected?.decision === 'approved' ? 'Approve account size' : 'Revoke new-trade permission'}
                onClose={() => {
                    if (!decide.isPending) setSelected(null);
                }}
                footer={
                    <Button
                        variant={selected?.decision === 'approved' ? 'primary' : 'danger'}
                        disabled={!selected || !acknowledged || reason.trim().length < 10 || decide.isPending}
                        onClick={() => {
                            if (selected)
                                decide.mutate({
                                    tradingAccountId: selected.account.tradingAccountId,
                                    decision: selected.decision,
                                    reason,
                                    acknowledgeRisk: true
                                });
                        }}
                    >
                        {decide.isPending ? 'Saving…' : selected?.decision === 'approved' ? 'Approve account size' : 'Revoke permission'}
                    </Button>
                }
            >
                {selected && (
                    <div className="space-y-4">
                        <p>
                            {selected.account.name} · {selected.account.brokerage} · Account #{selected.account.tradingAccountId} · User{' '}
                            {selected.account.userId}
                        </p>
                        {selected.decision === 'approved' ? (
                            <>
                                <p>
                                    Net value {money(selected.account.access.equity)} · Holdings{' '}
                                    {money(selected.account.access.grossPositionValue)}
                                </p>
                                <p>
                                    This approves the size of this specific account, including after reconnecting it. Unexplained holdings
                                    and pending reconciliation still block new entries. This decision never grants ownership of outside
                                    holdings.
                                </p>
                            </>
                        ) : (
                            <p>
                                This blocks new trades, including on accounts below $50,000. It does not cancel orders already at the
                                broker. Closing orders and cancellations remain available.
                            </p>
                        )}
                        <FormInput
                            id="broker-account-approval-reason"
                            label="Reason for this decision"
                            value={reason}
                            maxLength={1000}
                            onChange={(event) => setReason(event.target.value)}
                            helpText="At least 10 characters. Saved with your administrator identity and decision time."
                        />
                        <FormCheckbox
                            label="I reviewed this account and explicitly authorize this decision."
                            checked={acknowledged}
                            onChange={(event) => setAcknowledged(event.target.checked)}
                        />
                        {decide.error && (
                            <p role="alert" className="text-red-400">
                                {decide.error.message}
                            </p>
                        )}
                    </div>
                )}
            </Modal>
            <Modal
                isOpen={resolution !== null}
                title="Reconcile broker evidence"
                onClose={() => {
                    if (!reconcile.isPending) setResolution(null);
                }}
                footer={
                    <Button
                        variant="primary"
                        disabled={
                            reconcile.isPending ||
                            !acknowledged ||
                            !brokerReference.trim() ||
                            reason.trim().length < 10 ||
                            (isSettlement && (!(Number(quantity) > 0) || !Number.isFinite(Date.parse(eventTime))))
                        }
                        onClick={() => {
                            if (resolution)
                                reconcile.mutate({
                                    tradingAccountId: resolution.accountId,
                                    positionId: resolution.positionId,
                                    outcome,
                                    brokerReference,
                                    reason,
                                    acknowledgeBrokerEvidence: true,
                                    fees: Number(fees),
                                    ...(isSettlement
                                        ? { quantity: Number(quantity), occurredAt: Math.floor(Date.parse(eventTime) / 1000) }
                                        : {})
                                });
                        }}
                    >
                        {reconcile.isPending ? 'Reconciling…' : 'Record verified outcome'}
                    </Button>
                }
            >
                <div className="space-y-4">
                    <p>
                        Account #{resolution?.accountId} · Position #{resolution?.positionId}. Review the broker’s actual records before
                        making this decision.
                    </p>
                    <label className="block">
                        Verified outcome
                        <select
                            className="mt-1 block w-full rounded bg-gray-800 p-2"
                            value={outcome}
                            onChange={(event) => {
                                const value = event.target.value;
                                if (
                                    value === 'holdingsVerified' ||
                                    value === 'brokerClose' ||
                                    value === 'expiration' ||
                                    value === 'assignment' ||
                                    value === 'exercise'
                                )
                                    setOutcome(value);
                            }}
                        >
                            <option value="holdingsVerified">Holdings now match — clear reconciliation hold</option>
                            <option value="brokerClose">Import a specific broker closing order</option>
                            <option value="expiration">Broker-confirmed worthless expiration</option>
                            <option value="assignment">Broker-confirmed assignment</option>
                            <option value="exercise">Broker-confirmed exercise</option>
                        </select>
                    </label>
                    <FormInput
                        id="resolution-reference"
                        label={outcome === 'brokerClose' ? 'Broker closing order ID' : 'Broker record reference'}
                        value={brokerReference}
                        onChange={(event) => setBrokerReference(event.target.value)}
                        maxLength={120}
                    />
                    {isSettlement && (
                        <>
                            <FormInput
                                id="resolution-quantity"
                                label="Confirmed option contracts"
                                type="number"
                                value={quantity}
                                onChange={(event) => setQuantity(event.target.value)}
                            />
                            <FormInput
                                id="resolution-time"
                                label="Broker event time (your local time)"
                                type="datetime-local"
                                value={eventTime}
                                onChange={(event) => setEventTime(event.target.value)}
                            />
                            <FormInput
                                id="resolution-fees"
                                label="Broker fees ($)"
                                type="number"
                                value={fees}
                                onChange={(event) => setFees(event.target.value)}
                            />
                            <p className="text-amber-300">
                                Verified delivered shares become managed positions. The portfolio’s assigned-share liquidation setting may
                                then sell them automatically.
                            </p>
                        </>
                    )}
                    <FormInput
                        id="resolution-reason"
                        label="Evidence reviewed and reason"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        maxLength={1000}
                    />
                    <FormCheckbox
                        label="I verified the broker record and its account, instrument, quantity and outcome."
                        checked={acknowledged}
                        onChange={(event) => setAcknowledged(event.target.checked)}
                    />
                    {reconcile.error && (
                        <p role="alert" className="text-red-400">
                            {reconcile.error.message}
                        </p>
                    )}
                </div>
            </Modal>
        </section>
    );
}
